import * as React from 'react';
import { connect } from 'react-redux';
import { isBoolean, isEmpty, snakeCase } from 'lodash';

import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import CollapsibleSimple from 'components/collapsible/simple/CollapsibleSimple';
import SearchableDropdown from 'components/inputs/searchable-dropdown/SearchableDropdown';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import DiscoveryCreatorsFiltersAudienceAges from '../ages/DiscoveryCreatorsFiltersAudienceAges';
import DiscoveryCreatorsFiltersShare from '../share/DiscoveryCreatorsFiltersShare';
import ControlledRangeSelect from 'modules/discovery/components/range-select/ControlledRangeSelect';
import RangeSelectorTitle from 'modules/discovery/creators/components/filters/range-selector-header/RangeSelectorTitle';

import { __t, applyTranslations } from 'utils/language';
import { countrySearchToDropdownItems, wrappedCitySearchToDropdownItems } from 'utils/locations';
import { dataCommonToComponent, dataComponentToCommon } from 'utils/data/data';

import type {
  Data,
  DiscoveryCreatorsAgesFilter,
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilters,
  DiscoveryCreatorsShareData,
  DiscoveryFilterMinMax,
  MixedSelectDropdownItem
} from 'types';

import './discovery-creators-filters-instagram-audience.scss';

applyTranslations(require('./discovery-creators-filters-instagram-audience.i18n.json'));

type OwnProps = {
  filters: DiscoveryCreatorsFilters,
  lastStateReset: number,
  handleDiscoveryFiltersUpdate: (a: DiscoveryCreatorsFilter) => void
};

type DispatchProps = {
  dispatchSearchCities: (a: string) => Promise<Array<MixedSelectDropdownItem>>
};

type Props = OwnProps & DispatchProps;

type State = {
  isExpanded: boolean
};

const msgKey = 'discovery_creators_filters_instagram_audience';

class DiscoveryCreatorsFiltersInstagramAudience extends React.PureComponent<Props, State> {
  static defaultProps: OwnProps = {
    filters: {} as DiscoveryCreatorsFilters,
    lastStateReset: Date.now(),
    handleDiscoveryFiltersUpdate: () => {}
  };

  state = {
    isExpanded: false
  };

  handleUpdate = (result: DiscoveryCreatorsFilter): void => {
    const { handleDiscoveryFiltersUpdate } = this.props;

    if (handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate(result);
    }
  };

  // ==== Gender Ratio
  handleRatioSliderChangeComplete = (range: DiscoveryFilterMinMax, attribute?: string | null): void => {
    if (!attribute) {
      return;
    }

    const { audienceGenderRatio } = this.props.filters;
    const { male, female } = audienceGenderRatio;

    this.handleUpdate({
      audienceGenderRatio: {
        // reset both sliders to keep old logic
        male: {
          ...male,
          min: 0,
          max: 100
        },
        female: {
          ...female,
          min: 0,
          max: 100
        },
        // set updated entry
        [attribute]: {
          ...audienceGenderRatio[attribute],
          ...range
        }
      }
    });
  };

  // ==== Multiselects
  handleItemsUpdate = (updatedItems: Array<MixedSelectDropdownItem>, data?: Data | null): void => {
    const { audienceCredibility, audienceReachability } = this.props.filters;

    if (!data || !data.name) {
      return;
    }

    let newData = {};

    switch (data.name) {
      case 'credibility':
        newData = {
          audienceCredibility: audienceCredibility.map(credibilityEntry => ({
            ...credibilityEntry,
            selected: (
              updatedItems.find((item) => item.identifier === credibilityEntry.identifier) || {}
            ).isSelected
          }))
        };
        break;
      case 'reachability':
        newData = {
          audienceReachability: audienceReachability.map(reachabilityEntry => ({
            ...reachabilityEntry,
            selected: (
              updatedItems.find((item) => item.identifier === reachabilityEntry.identifier) || {}
            ).isSelected
          }))
        };
        break;
      case 'interests':
        newData = {
          audienceInterests: updatedItems
        };
        break;
      default:
        break;
    }

    this.handleUpdate({
      ...newData
    });
  };

  // ==== Locations
  handleLocationChange = (items: Array<MixedSelectDropdownItem>, data?: Data | null) => {
    if (!data || !data.name) {
      return;
    }

    const { filters } = this.props;
    const countriesSelected = items.filter(item => item.isSelected);
    const countriesShareCheckbox = filters.audienceCountriesShare.checkbox || {};

    // deselect the "share" checkbox under certain conditions
    // this keeps the query object builder from including the prop
    this.handleUpdate({
      [data.name]: countriesSelected.map(dataComponentToCommon),
      audienceCountriesShare: {
        ...filters.audienceCountriesShare,
        checkbox: {
          ...countriesShareCheckbox,
          selected: !isEmpty(countriesSelected) && countriesShareCheckbox.selected
        }
      }
    });
  };

  handleToggle = (isExpanded: boolean): void => {
    this.setState({
      isExpanded
    });
  };

  handleCountriesShareUpdate = (share: DiscoveryCreatorsShareData) => {
    this.handleUpdate({
      audienceCountriesShare: share
    });
  };

  // ==== Ages
  handleAgeSectionUpdate = (data: Partial<DiscoveryCreatorsAgesFilter>) => {
    const payload: Partial<DiscoveryCreatorsFilter> = {};

    if (data.items) {
      payload.audienceAges = data.items;
    }

    if (data.share) {
      payload.audienceAgesShare = data.share;
    }

    this.handleUpdate(payload);
  };

  renderTitle(title: string, tooltip?: string): React.ReactNode {
    return (
      <RangeSelectorTitle
        title={title}
        tooltip={tooltip}
      />
    );
  }

  renderRatioSlider(type: 'male' | 'female'): React.ReactNode {
    const data = this.props.filters.audienceGenderRatio[type];

    const valueTransformer = (range) => `${range.min}% – ${range.max}%`;

    return (
      <ControlledRangeSelect
        key={`instagram-gender-ratio-${type}`}
        title={__t(`${msgKey}.gender_ratio_${type}_title`)}
        tooltip={__t(`${msgKey}.gender_ratio_${type}_tooltip`)}
        titleValueTransformer={valueTransformer}
        data={data}
        attribute={type}
        onChangeComplete={this.handleRatioSliderChangeComplete}
      />
    );
  }

  renderMixedDropdown(key: string, data: Array<MixedSelectDropdownItem>, withSortable?: boolean): React.ReactNode {
    return (
      <MixedSelectDropdown
        key={`discovery-creators-filters-content__${key}-${this.props.lastStateReset}`}
        name={`discovery-creators-filters-content__${key}-${this.props.lastStateReset}`}
        items={data}
        withMulti
        withRemovable
        withSortable={isBoolean(withSortable) ? withSortable : true}
        withCheckboxes
        onItemsToggle={this.handleItemsUpdate}
        onItemsRemove={this.handleItemsUpdate}
        onItemsRemoveAll={this.handleItemsUpdate}
        data={{
          name: key
        }}
      />
    );
  }

  renderCredibility(): React.ReactNode {
    const data = this.props.filters.audienceCredibility.map(dataCommonToComponent);

    return (
      <React.Fragment>
        {this.renderTitle(
          __t(`${msgKey}.credibility_score_title`),
          __t(`${msgKey}.credibility_score_tooltip`)
        )}
        <VerticalSpacing type="small" />
        {this.renderMixedDropdown('credibility', data, false)}
      </React.Fragment>
    );
  }

  renderReachability(): React.ReactNode {
    const data = this.props.filters.audienceReachability.map(dataCommonToComponent);

    return (
      <React.Fragment>
        {this.renderTitle(
          __t(`${msgKey}.reachability_score_title`),
          __t(`${msgKey}.reachability_score_tooltip`)
        )}
        <VerticalSpacing type="small" />
        {this.renderMixedDropdown('reachability', data, false)}
      </React.Fragment>
    );
  }

  // TODO: COLLAB-14149 could be deleted completely if needed
  private renderInterests(): React.ReactNode {
    const data = this.props.filters.audienceInterests;

    return (
      <React.Fragment>
        {this.renderTitle(
          __t(`${msgKey}.top_interests_title`),
          __t(`${msgKey}.top_interests_tooltip`)
        )}
        <VerticalSpacing type="small" />
        {this.renderMixedDropdown('interests', data)}
      </React.Fragment>
    );
  }

  renderLocation(
    key: 'audienceCountries' | 'audienceCities',
    dataFetcher: (a: string) => Promise<Array<MixedSelectDropdownItem>>
  ): React.ReactNode {
    const { audienceCountries, audienceCities } = this.props.filters;
    const items = key === 'audienceCountries' ? audienceCountries : audienceCities;

    return (
      <React.Fragment>
        {this.renderTitle(
          __t(`${msgKey}.top_${snakeCase(key)}_title`)
        )}
        <VerticalSpacing type="small" />
        <SearchableDropdown
          key={`discovery-creators-filters-content__${key}-${this.props.lastStateReset}`}
          items={items.map(dataCommonToComponent)}
          dataFetcher={dataFetcher}
          data={{
            name: key
          }}
          onItemsUpdate={this.handleLocationChange}
        />
      </React.Fragment>
    );
  }

  renderCountriesShare(): React.ReactNode {
    const { filters } = this.props;

    return (
      <React.Fragment>
        <VerticalSpacing type="medium" />
        <DiscoveryCreatorsFiltersShare
          filterName="audienceCountriesShare"
          filter={filters.audienceCountriesShare}
          type="countries"
          isDisabled={isEmpty(filters.audienceCountries)}
          onHandleUpdate={this.handleCountriesShareUpdate}
        />
      </React.Fragment>
    );
  }

  renderAgesSection(): React.ReactNode {
    const { filters } = this.props;
    const filtersPayload = {
      items: filters.audienceAges,
      share: filters.audienceAgesShare
    };

    return (
      <DiscoveryCreatorsFiltersAudienceAges
        name="instagram"
        filters={filtersPayload}
        onHandleUpdate={this.handleAgeSectionUpdate}
      />
    );
  }

  render(): React.ReactNode {
    const { filters } = this.props;
    const { isExpanded } = this.state;
    const searchByName = filters.searchByName;

    return (
      <CollapsibleSimple
        title={__t(`${msgKey}.section_label`)}
        contentClassName="discovery-creators-filters-instagram-audience"
        isExpanded={isExpanded && isEmpty(searchByName)}
        isDisabled={!isEmpty(searchByName)}
        onToggle={this.handleToggle}
      >
        {this.renderAgesSection()}
        <VerticalSpacing type="large" />
        {this.renderRatioSlider('female')}
        <VerticalSpacing type="large" />
        {this.renderRatioSlider('male')}
        <VerticalSpacing type="xlarge" />
        {this.renderLocation('audienceCountries', countrySearchToDropdownItems)}
        {this.renderCountriesShare()}
        <VerticalSpacing type="large" />
        {this.renderLocation('audienceCities', this.props.dispatchSearchCities)}
        <VerticalSpacing type="large" />
        {this.renderCredibility()}
        <VerticalSpacing type="large" />
        {this.renderReachability()}
      </CollapsibleSimple>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSearchCities: wrappedCitySearchToDropdownItems(dispatch)
});

export default connect(null, mapDispatchToProps)(DiscoveryCreatorsFiltersInstagramAudience);
